import { useEffect } from 'react'
import { path, pipe, find, propEq, isEmpty, filter, allPass, anyPass } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { select } from '@/store'
import useMediaQuery from '@/hooks/use-media-query'
import { colors, breakpoints, fontWeights } from '@/styles'
import Tables from './tables'
import DocumentsIcon from '../documents-icon.svg'
import TenancyIcon from '../tenancy-icon.svg'
import SupplementaryIcon from '../supplementary-icon.svg'
import RedFolderIcon from '../red-folder-icon.svg'
import { DOCUMENT_STATUS, SIGNATURE_STATUS } from '../tenancy-documents/status-components'

const Container = styled.div`
  max-width: 1192px;
  margin: 30px 34px;
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 50px 3fr;
  width: 100%;
  ${props => props.isMobile && `display: block;`};
`

const GridOne = styled.div`
  grid-column-start: 1;
`

const GridTwo = styled.div`
  grid-column-start: 2;
  max-width: 928px;
`

const VerticalLine = styled.span`
  display: block;
  width: 38px;
  margin-top: -10px;
  background: linear-gradient(${colors.grey88}, ${colors.grey88}) no-repeat center/2px 100%;
`

const VerticalLineOne = styled(VerticalLine)`
  height: 36px;
`

const VerticalLineTwo = styled(VerticalLine)`
  height: 146px;
`

const HeaderWrapper = styled.div`
  display: flex;
`

const HeadingContainer = styled.div`
  display: flex;
  height: 20px;
  margin-top: 10px;
  margin-bottom: 54px;
`

const Heading = styled.h5`
  margin-left: 10px;
  font-size: 20px;
  font-weight: bold;
`

const DocumentIconImage = styled.img`
  margin-left: -4px;
`

const TableTitle = styled.h5`
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-bottom: 12px;
  }
`
const EmptyText = styled.p`
  font-weight: ${fontWeights.regular};
  font-size: 14px;
  line-height: 16px;
  color: ${colors.grey56};
  margin: 20px 0;
`

const isSentOrSigned = pipe(
  anyPass([propEq('state_machine', 'sent'), propEq('state_machine', 'signed')])
)

const getTenancyPackageFile = pipe(
  find(allPass([propEq('document_type', 'tenancy_agreement'), isSentOrSigned]))
)
const getSupplementaryPackageFile = pipe(
  filter(allPass([propEq('document_type', 'supporting_document'), isSentOrSigned]))
)

const Documents = ({ contractId }) => {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const contract = useSelector(path(['contract', contractId, 'contract']))
  const { packageFiles = [], signers } = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId })
  )

  const isNativeContract = contract?.contract_structure === 'native' ? true : false
  const tenancyAgreementPackageFile =
    (isNativeContract ? contract : getTenancyPackageFile(packageFiles)) ?? {}
  const supplementaryFiles = getSupplementaryPackageFile(packageFiles) ?? []
  const tenancyAgreementSigners = signers?.[tenancyAgreementPackageFile?.id]
  const noTenancyAgreement = isEmpty(tenancyAgreementPackageFile)
  const noSupplementaryFiles = isEmpty(supplementaryFiles)

  const noContract = !contract || !contract?.id

  useEffect(() => {
    async function fetchPackageInfo() {
      await dispatch.contract.getPackage({ contractId })
    }
    if (contractId) fetchPackageInfo()
  }, [dispatch.contract, contractId])

  useEffect(() => {
    if (isEmpty(tenancyAgreementPackageFile) && !noContract && !isNativeContract) {
      history.replace(`/`)
    }
  }, [tenancyAgreementPackageFile, history, contract, isNativeContract])

  return (
    <Container>
      <GridContainer isMobile={isMobile}>
        {!isMobile && (
          <GridOne>
            <DocumentIconImage src={DocumentsIcon} alt="documents" />
            <VerticalLineOne />
            <img src={TenancyIcon} alt="tenancy" />
            {!isNativeContract && (
              <>
                <VerticalLineTwo />
                <img src={SupplementaryIcon} alt="supplementary" />
              </>
            )}
          </GridOne>
        )}
        <GridTwo>
          <HeaderWrapper>
            <HeadingContainer>
              {isMobile && <img src={RedFolderIcon} alt="icon" />}
              <Heading>{contract?.buildings?.full_address}</Heading>
            </HeadingContainer>
          </HeaderWrapper>
          {noTenancyAgreement ? (
            <>
              <TableTitle>
                {t('l.contract_manager.documents.tenancy_agreement.title', 'Tenancy Agreement')}
              </TableTitle>
              <EmptyText>
                {t(
                  'l.contract_manager.documents.no_addendums.title',
                  'No addendums have been added to your contract.'
                )}
              </EmptyText>
            </>
          ) : (
            <Tables
              header
              primary
              title={t('l.contract_manager.documents.tenancy_agreement.title', 'Tenancy Agreement')}
              documentStatus={DOCUMENT_STATUS[tenancyAgreementPackageFile.state_machine]}
              signatureStatus={SIGNATURE_STATUS[tenancyAgreementPackageFile.signature_status]}
              contract={contract}
              isMobile={isMobile}
              packageFile={tenancyAgreementPackageFile}
              order={0}
              signers={tenancyAgreementSigners}
              packageFiles={packageFiles}
            />
          )}
          {noSupplementaryFiles ? (
            <>
              <TableTitle>Supplementary Documents</TableTitle>
              <EmptyText>
                {t(
                  'l.contract_manager.documents.no_addendums.title',
                  'No addendums have been added to your contract.'
                )}
              </EmptyText>
            </>
          ) : (
            supplementaryFiles.map((item, i) => {
              return (
                <Tables
                  index={i}
                  key={i}
                  header={i === 0}
                  secondary
                  title="Supplementary Documents"
                  documentStatus={DOCUMENT_STATUS[item.state_machine]}
                  signatureStatus={SIGNATURE_STATUS[item.signature_status]}
                  contract={contract}
                  isMobile={isMobile}
                  packageFile={item}
                  order={i + 1}
                  signers={signers?.[item?.id]}
                  packageFiles={packageFiles}
                />
              )
            })
          )}
        </GridTwo>
      </GridContainer>
    </Container>
  )
}

export default Documents
